import { PlusIcon } from "@heroicons/react/20/solid";
import { SchedulerLesson } from "../../../models/schedule/lesson";
import { ContentFactory } from "../content-factory/ContentFactory";
import { IContent } from "../content-factory/IContent";
import {
  ISchedulerViewMode,
  SchedulerViewConfiguration,
} from "./ISchedulerViewMode";
import { ViewModeWeek } from "./viewModeWeek";
import { HighlightTeacher } from "./highlightTeacherDecorator";
import { ComponentColor } from "../../../helpers/constants/types";

/**
 * Decorates the viewModeWeek class with the possibility to display schedules of subtitute teachers
 */
export class ViewModeSubstituteSelectionWeek
  implements ISchedulerViewMode<SchedulerLesson>
{
  private viewModeWeek = new HighlightTeacher(
    new ViewModeWeek(false, true),
    this.selectedSubstituteId
  );

  constructor(private selectedSubstituteId: string) {}

  getWeekday(
    startDate: Date,
    day: number,
  ): { id: number; name: string; shortName: string } {
    return this.viewModeWeek.getWeekday(startDate, day);
  }

  toggleActive(
    day: number,
    duration: number,
    lessons: SchedulerLesson[][],
  ): SchedulerLesson[] {
    return this.viewModeWeek.toggleActive(
      day,
      duration,
      this.modifyLessons(lessons),
    );
  }

  setDay(
    day: number,
    value: boolean,
    lessons: SchedulerLesson[][],
  ): SchedulerLesson[] {
    return this.viewModeWeek.setDay(day, value, this.modifyLessons(lessons));
  }

  displayLessons(
    startDate: Date,
    lessons: SchedulerLesson[][],
    page = 0,
  ): SchedulerLesson[][] {
    const lessonSlice = this.viewModeWeek.displayLessons(
      startDate,
      lessons,
      page,
    ) as SchedulerLesson[][];

    return this.modifyLessons(lessonSlice);
  }

  hasPage(page: number, lessons: SchedulerLesson[][]): boolean {
    return this.viewModeWeek.hasPage(page, lessons);
  }

  getLessonComponentColor(lesson: SchedulerLesson): ComponentColor {
    return this.viewModeWeek.getLessonComponentColor(lesson);
  }

  getLessonComponentStyle(lesson: SchedulerLesson): string {
    return this.viewModeWeek.getLessonComponentStyle(lesson);
  }

  getLessonContent(
    lesson: SchedulerLesson,
    viewConfiguration: SchedulerViewConfiguration,
  ): IContent | null {
    if (
      lesson.teacherApplications?.length &&
      lesson.teacherApplications.some(
        (application) => application.id === this.selectedSubstituteId,
      )
    ) {
      return ContentFactory.createContainer()
        .withChildren([
          ContentFactory.createIcon(<PlusIcon />)
            .withCondition(() => !lesson.isActive)
            .withStyle("text-th-accent-900"),
          ContentFactory.createText(
            viewConfiguration.size === "small"
              ? (lesson.name ?? "")[0]
              : lesson.name ?? "",
          ),
        ])
        .withStyle(
          `flex items-center justify-center ${
            !lesson.isActive && lesson.canActivate && "-ml-4"
          }`,
        );
    }

    return this.viewModeWeek.getLessonContent(lesson, viewConfiguration);
  }

  /**
   * Modifies the lessons with parameters according to the selected teacher
   */
  private modifyLessons(lessons: SchedulerLesson[][]): SchedulerLesson[][] {
    return lessons.map((dayLessons) =>
      dayLessons.map((lesson) => {
        const lessonClone = { ...lesson };
        // Only lessons that are active can be deactivated and reactivated
        lessonClone.canActivate =
          lessonClone.teacherApplications?.some(
            (application) => application.id === this.selectedSubstituteId,
          ) ?? false;

        return lessonClone;
      }),
    );
  }
}
